import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
} from "@tanstack/react-query";
import bodycamClient from "@/shared/api/clients/bodycamClient";
import type { EvidenceListResponse } from "../../../shared/interfaces/EvidenceListResponse";
import { VideoStatus } from "../../../shared/interfaces/VideoData";

export type EvidenceSortField = "createdAt" | "recordedAt";
export type EvidenceSortDirection = "asc" | "desc";

export interface EvidenceSort {
  sortBy: EvidenceSortField;
  sortOrder: EvidenceSortDirection;
}

/** The locker opens newest-first on upload date, matching the report list. */
export const DEFAULT_EVIDENCE_SORT: EvidenceSort = {
  sortBy: "createdAt",
  sortOrder: "desc",
};

/**
 * Computes the next sort state when a date chip is clicked, mirroring the
 * home page's chips: clicking the already-active field flips direction
 * (newest↔oldest); selecting a different field starts newest-first (desc).
 */
export function nextEvidenceSort(
  current: EvidenceSort,
  field: EvidenceSortField,
): EvidenceSort {
  if (current.sortBy === field) {
    return {
      sortBy: field,
      sortOrder: current.sortOrder === "asc" ? "desc" : "asc",
    };
  }
  return { sortBy: field, sortOrder: "desc" };
}

export interface EvidenceListFilters {
  // Two independent date ranges (ISO strings). "created" = upload date,
  // "recorded" = recorded date; either may be set on its own.
  createdFrom?: string;
  createdTo?: string;
  recordedFrom?: string;
  recordedTo?: string;
  textQuery?: string;
  sortBy: EvidenceSortField;
  // Sort direction on the chosen date dimension. Sent to the backend, which
  // flips its DynamoDB scan accordingly; "desc" (newest-first) is the default.
  sortOrder: EvidenceSortDirection;
  // Which slice of evidence to list: ingested videos or AI-rejected
  // (never-fetched) candidates. Maps directly to the `videoState` query param.
  videoState: "ingested" | "rejected";
  // Page size. When provided, it must be an integer from 1 through 100; the
  // server defaults to 50 when it is omitted.
  limit?: number;
  // Uploader scoping. `userId` is a comma-joined list of uploader ids — the
  // list is narrowed to evidence uploaded by any of them (My Evidence tab and
  // the "Uploaded By" filter). `excludeUserId` narrows to evidence uploaded by
  // anyone *other* than that id (the default "Evidence Shared With Me" view).
  userId?: string;
  excludeUserId?: string;
}

export const evidenceKeys = {
  all: ["evidence"] as const,
  list: (f: EvidenceListFilters) => [...evidenceKeys.all, "list", f] as const,
  poll: (f: EvidenceListFilters) => [...evidenceKeys.all, "poll", f] as const,
  thumbnail: (evidenceId: string) =>
    [...evidenceKeys.all, "thumbnail", evidenceId] as const,
};

export async function fetchEvidencePage(
  filters: EvidenceListFilters,
  nextToken: string | undefined,
  signal?: AbortSignal,
): Promise<EvidenceListResponse> {
  if (
    filters.limit !== undefined &&
    (!Number.isFinite(filters.limit) ||
      !Number.isInteger(filters.limit) ||
      filters.limit < 1 ||
      filters.limit > 100)
  ) {
    throw new RangeError("Evidence list limit must be an integer from 1 to 100");
  }

  const params: Record<string, string | undefined> = {
    nextToken,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    videoState: filters.videoState,
  };
  if (filters.createdFrom) params.createdFrom = filters.createdFrom;
  if (filters.createdTo) params.createdTo = filters.createdTo;
  if (filters.recordedFrom) params.recordedFrom = filters.recordedFrom;
  if (filters.recordedTo) params.recordedTo = filters.recordedTo;
  if (filters.textQuery) params.textQuery = filters.textQuery;
  if (filters.limit !== undefined) params.limit = String(filters.limit);
  if (filters.userId) params.userId = filters.userId;
  if (filters.excludeUserId) params.excludeUserId = filters.excludeUserId;

  const response = await bodycamClient.get<EvidenceListResponse>("/evidence", {
    params,
    signal,
  });
  return response.data;
}

export function evidenceListInfiniteQuery(filters: EvidenceListFilters) {
  return infiniteQueryOptions({
    queryKey: evidenceKeys.list(filters),
    queryFn: ({ pageParam, signal }) => fetchEvidencePage(filters, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextToken || undefined,
    staleTime: 0,
    // Drop the cache on unmount: the old hook fetched fresh on every mount.
    gcTime: 0,
    // The old fetch had no retry — errors toasted immediately.
    retry: false,
  });
}

/**
 * Drops every loaded page after the first, so a refresh refetches a single
 * page instead of replaying the whole cursor chain — while the page-1 rows
 * stay visible during the round trip (no blank flash, unlike resetQueries).
 */
export function trimToFirstPage(
  data: InfiniteData<EvidenceListResponse, string | undefined>,
): InfiniteData<EvidenceListResponse, string | undefined> {
  if (data.pages.length <= 1) return data;
  return {
    pages: data.pages.slice(0, 1),
    pageParams: data.pageParams.slice(0, 1),
  };
}

/**
 * Folds a page-1 poll response into the loaded pages, reproducing the old
 * "merge" mode exactly: known rows are refreshed in place across ALL pages,
 * genuinely new items are prepended to page 1, and pageParams (the pagination
 * cursors) are left untouched — fresh.nextToken is deliberately ignored so a
 * page-1 poll never overwrites the cursor for loaded pages.
 *
 * A known row takes the WHOLE fresh record (not just status): as a video
 * ingests, the backend fills in fileName, duration, file URL, etc. The one
 * exception is an optimistically-FETCHING card (set by the hook's
 * markVideoFetching after a fetch is requested) — it is kept as FETCHING while
 * the backend still reports it NOT_EVIDENCE, so the in-progress state doesn't
 * flicker back until ingestion actually starts.
 *
 * On the unfetched (rejected) tab, pass `dropMissingFetching: true`: a locally
 * FETCHING row that is absent from the poll response has left the rejected set,
 * so it is dropped (it now belongs on the Ingested tab). Absence alone is never
 * enough to remove a non-FETCHING row — page-2+ rows are absent from a page-1
 * poll too — so pagination stays intact.
 */
export function mergePollResults(
  existing: InfiniteData<EvidenceListResponse, string | undefined>,
  fresh: EvidenceListResponse,
  { dropMissingFetching = false }: { dropMissingFetching?: boolean } = {},
): InfiniteData<EvidenceListResponse, string | undefined> {
  const freshList = fresh.evidence ?? [];
  const freshById = new Map(freshList.map((e) => [e.evidenceId, e]));
  const freshIds = new Set(freshList.map((e) => e.evidenceId));
  const knownIds = new Set(
    existing.pages.flatMap((p) => (p.evidence ?? []).map((e) => e.evidenceId)),
  );
  const toAdd = freshList.filter((e) => !knownIds.has(e.evidenceId));

  const pages = existing.pages.map((page, index) => {
    let updated = (page.evidence ?? []).map((e) => {
      const freshItem = freshById.get(e.evidenceId);
      if (!freshItem) return e;
      // Keep an optimistically-FETCHING card until the backend stops reporting
      // it as NOT_EVIDENCE (i.e. ingestion has actually begun).
      if (
        e.status === VideoStatus.FETCHING &&
        freshItem.status === VideoStatus.NOT_EVIDENCE
      ) {
        return e;
      }
      return { ...e, ...freshItem };
    });
    if (dropMissingFetching) {
      updated = updated.filter(
        (e) => e.status !== VideoStatus.FETCHING || freshIds.has(e.evidenceId),
      );
    }
    return {
      ...page,
      evidence: index === 0 && toAdd.length ? [...toAdd, ...updated] : updated,
    };
  });

  return { ...existing, pages };
}

/**
 * Presigned URL for a clip's poster frame. Served by the bodycam API — the
 * incidents API doesn't proxy this route, so any incidents surface that wants a
 * frame reaches across to the evidence service for it, keyed on the upstream
 * evidenceId.
 */
export async function fetchEvidenceThumbnail(
  evidenceId: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = await bodycamClient.get<{ url: string }>("/thumbnail", {
    params: { video_id: evidenceId },
    signal,
  });
  return response.data.url;
}

export function evidenceThumbnailQuery(evidenceId: string) {
  return queryOptions({
    queryKey: evidenceKeys.thumbnail(evidenceId),
    queryFn: ({ signal }) => fetchEvidenceThumbnail(evidenceId, signal),
    // The URL is presigned and the frame never changes, so hold it well past a
    // dialog open/close cycle: the same clip shown in the videos section, the
    // attach picker and the wizard costs one request, not three.
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // A clip with no extracted frame answers 404 forever — retrying it three
    // times per card just multiplies the failure across a 24-card grid.
    retry: false,
  });
}
